/* eslint-disable @nx/enforce-module-boundaries */
import {
  Brifecase,
  Currency,
  Industry,
  SaveJobs,
  JobLocationIcon,
  CompanyIndustryIcon,
} from '@mybridge/icons';
import { SavedJobIcon } from '@mybridge/icons/SavedJob';
import {
  Box,
  Button,
  HStack,
  Heading,
  IconButton,
  Image,
  Link,
  SeeMore,
  Stack,
  Text,
  VStack,
  Grid,
} from '@mybridge/ui';
import { forwardRef, useContext } from 'react';
import {
  convertHTMLToPlainText,
  currencyFormatter,
  formatLocation,
} from 'v4/lib/commons';
import { isMobile } from 'react-device-detect';
import { EMPLOYMENT_CHOICES, WORKPLACE_TYPE } from 'v4/lib/data';
import { ForceSignInWrapper } from 'v4/components/force-sign-in-wrapper';
import { useTranslator } from '@mybridge/ui/custom-hooks/useTranslation';
import { useNumberFormatter } from '@mybridge/ui/custom-hooks/useNumberFormatter';
import moment from 'moment';
import { useSelector } from 'react-redux';
import { useRouter } from 'next/router';
import { JobsListingContext } from 'v4/snippets/jobs/context';

export const SuggestedJobCard = forwardRef(
  (
    {
      isApplied = false,
      onApplyClick,
      job,
      onViewDetailsClick,
      onSaveClick,
      saveLoading,
      isSaved = false,
      href,
      ...props
    },
    ref
  ) => {
    const {
      hiring_company_details: company,
      currency_choice: currency,
      compensation_range_min_amt: minSalary,
      compensation_range_max_amt: maxSalary,
      benefits,
      employment_type: empType,
      job_description: description,
      payment_choice,
      remote,
      how_to_apply,
      how_to_apply_text,
    } = job ?? {};

    const { appliedJobsListing } = useContext(JobsListingContext);
    const applied = appliedJobsListing?.find(
      (aj) => aj?.job_details?.id === job?.id
    );

    const formatDate = (dateString) => {
      const options = { year: 'numeric', month: 'short', day: 'numeric' };
      return new Date(dateString).toLocaleDateString('en-US', options);
    };

    const prevDefaultCompanyImg = "https://cdn.bybk.org/media/placeholder/logo_placeholder_image.png"
    const defaultCompanyImg = "https://cdn.bybk.org/media/placeholder/logo-placeholder-image.png"

    
    const jobLocation = formatLocation(job);
    const descPlainText = convertHTMLToPlainText(description ?? '');
    const benefitsValue=benefits?.map?.((b) => b?.name)?.join(', ');
    const {companyPageList} = useSelector((state) => state.companyPage);
    const {formatNumber}=useNumberFormatter();
    const isPrivatePage = companyPageList?.some((page) => page?.id === job?.hiring_company_details?.id);
    const { push } = useRouter();
    const jsonString = job?.how_to_apply_text
      ?.replace(/'/g, '"')
      ?.replace(/^\[|\]$/g, '')
      .split(',')[0] // Split by comma and take the first element
      .trim(); // Change single quotes to double and remove brackets
      const { t } = useTranslator();
    return (
      <Stack h="100%" {...props}>
        <HStack alignItems="flex-start">
          <HStack flex={1}>
            <Image
              border="1px solid"
              borderColor="blackAlpha.100"
              src={
                company?.image?.includes('logo_placeholder_image.png') || company?.image?.includes('logo-placeholder-image.png')
                  ? defaultCompanyImg
                  : company?.image ?? defaultCompanyImg
              }
              w="12"
              h="12"
              borderRadius="50%"
              alt={company?.name}
            />
            <VStack align="start" spacing={0} ml={2}>
              {' '}
              <Heading
                {...(href ? { as: Link, href } : {})}
                color="#2E2E2A"
                size="sm"
                fontWeight="500"
              >
                {job?.title}
              </Heading>
              <Text size="14px" color="#5B5B5B" fontWeight="500">
                {job?.days_posted === 0
                  ? moment(job?.published_date).local().fromNow()
                  : `${formatNumber(job?.days_posted)} ${job?.days_posted === 1 ? (t('jobs.dayAgo') || 'day ago') : (t('jobs.daysAgo') || 'days ago')}`
                }
              </Text>{' '}
            </VStack>
          </HStack>
        </HStack>
        <Stack h="100%" spacing={0}>
          <Stack spacing={2}>
            <HStack
              alignItems="flex-start"
              cursor="pointer"
              onClick={() => {
                isPrivatePage
                  ? push(`/page/private/${job?.hiring_company_details?.id}`)
                  : push(`/c/${job?.hiring_company_details?.slug}`);
              }}
            >
              <CompanyIndustryIcon color="#9F9F9F" />
              <Text display="inline" fontSize="sm" isTruncated>
                {company?.name}
              </Text>
            </HStack>
            {jobLocation ? (
              <HStack alignItems="flex-start">
                <>
                  <JobLocationIcon color="#9F9F9F" />
                  <Text display="inline" fontSize="sm" isTruncated>
                    {jobLocation}
                  </Text>
                </>
              </HStack>
            ) : (
              <></>
            )}
            {minSalary > 0 && (
              <HStack alignItems="flex-start">
                <Box pt={'2px'}>
                  <Currency color="#9F9F9F" />
                </Box>
                {maxSalary !== null || maxSalary > 0 ? (
                  <Text fontSize="sm" isTruncated>
                    {currencyFormatter(minSalary, currency)} to{' '}
                    {currencyFormatter(maxSalary, currency)}{' '}
                    {payment_choice
                      .toLowerCase()
                      .replace('ANNUALLY', 'Annually')}
                  </Text>
                ) : (
                  <Text fontSize="sm" isTruncated>
                    {currencyFormatter(minSalary, currency)}{' '}
                    {payment_choice
                      .toLowerCase()
                      .replace('ANNUALLY', 'Annually')}
                  </Text>
                )}
              </HStack>
            )}
            {benefits?.length > 0 && (
              <HStack alignItems="flex-start">
                <Box pt={'2px'}>
                  <Industry color="#9F9F9F" />
                </Box>
                <Text fontSize="sm" isTruncated={!isMobile}>
                  {benefitsValue}
                </Text>
              </HStack>
            )}

            <HStack alignItems="flex-start">
              <Box pt={'2px'}>
                <Brifecase color="#9F9F9F" />
              </Box>
              <Text fontSize="sm" isTruncated>
                {EMPLOYMENT_CHOICES?.find((ec) => ec.value === empType)?.label}{' '}
                | {WORKPLACE_TYPE?.find((ec) => ec.value === remote)?.label}
              </Text>
            </HStack>
            {/* {description?.length > 0 ? (
                <SeeMore linkProps={{ href }} text={descPlainText} mt={2} />
              ) : (
                ''
              )} */}
          </Stack>
          <HStack
            h="100%"
            alignItems="flex-end"
            justifyContent="flex-start"
            pt={4}
          >
            <ForceSignInWrapper>
              {isMobile ? (
                <Grid
                  w="100%"
                  templateColumns="repeat(3, 1fr)" // 3 buttons per row
                  gap={4} // Space between items
                  justifyContent="center" // Center align the grid
                  justifyItems="center" // Center items in each cell
                >
                  {isApplied ? (
                    <Button
                      fontWeight="bold"
                      border="1px solid #38A169"
                      borderRadius="50px"
                      bg="none"
                      fontSize="14px"
                      color="green.500"
                      minWidth="120px"
                    >
                      {t('jobs.applied') || 'Applied'}
                    </Button>
                  ) : how_to_apply === 'EXTERNAL' ? (
                    <Button
                      onClick={() => {
                        const cleanedUrl = jsonString
                          .replace(/['"]/g, '')
                          .trim();
                        const jsonStrings =
                          cleanedUrl.startsWith('https://') ||
                          cleanedUrl.startsWith('http://')
                            ? cleanedUrl
                            : `https://${cleanedUrl.replace(
                                /^https?:\/\//,
                                ''
                              )}`;

                        if (
                          cleanedUrl &&
                          cleanedUrl !== 'https://' &&
                          cleanedUrl !== 'http://'
                        ) {
                          window?.open(jsonStrings, '_blank');
                        } else {
                          console.error('Invalid URL:', jsonStrings);
                        }
                      }}
                      target="_blank"
                      border="1px solid gray"
                      borderRadius="50px"
                      bg="none"
                      color="#5B5B5B"
                      fontSize="14px"
                      minWidth="120px"
                    >
                      {t('pageJobs.apply') || 'Apply'}
                    </Button>
                  ) : (
                    <ForceSignInWrapper>
                      <Button
                        onClick={onApplyClick}
                        border="1px solid gray"
                        borderRadius="50px"
                        bg="none"
                        color="#5B5B5B"
                        fontSize="14px"
                        minWidth="120px"
                      >
                        {t('pageJobs.apply') || 'Apply'}
                      </Button>
                    </ForceSignInWrapper>
                  )}
                  <Button
                    onClick={onViewDetailsClick}
                    border="1px solid gray"
                    borderRadius="50px"
                    bg="none"
                    color="#5B5B5B"
                    fontSize="14px"
                    minWidth="120px"
                  >
                    {t('pageJobs.viewDetails') || 'View Details'}
                  </Button>
                  <IconButton
                    isLoading={saveLoading}
                    onClick={onSaveClick}
                    border="1px solid gray"
                    borderRadius="50px"
                    bg="none"
                    color="#5B5B5B"
                    fontSize="14px"
                  >
                    {isSaved ? (
                      <SavedJobIcon width="20" height="20" />
                    ) : (
                      <SaveJobs width="20" height="20" />
                    )}
                  </IconButton>
                </Grid>
              ) : (
                <HStack spacing={2}>
                  {isApplied ? (
                    <Button
                      fontWeight="semibold"
                      border="1px solid #38A169"
                      borderRadius="50px"
                      bg="none"
                      fontSize="14px"
                      color="green.500"
                    >
                      {t('jobs.applied') || "Applied"} | {formatDate(applied?.applied_date)}
                    </Button>
                  ) : how_to_apply === 'EXTERNAL' ? (
                    <Button
                      onClick={() => {
                        const cleanedUrl = jsonString
                          .replace(/['"]/g, '')
                          .trim();
                        const jsonStrings =
                          cleanedUrl.startsWith('https://') ||
                          cleanedUrl.startsWith('http://')
                            ? cleanedUrl
                            : `https://${cleanedUrl.replace(
                                /^https?:\/\//,
                                ''
                              )}`;

                        if (
                          cleanedUrl &&
                          cleanedUrl !== 'https://' &&
                          cleanedUrl !== 'http://'
                        ) {
                          window?.open(jsonStrings, '_blank');
                        } else {
                          console.error('Invalid URL:', jsonStrings);
                        }
                      }}
                      target="_blank"
                      border="1px solid gray"
                      borderRadius="50px"
                      bg="none"
                      color="#5B5B5B"
                      fontSize="14px"
                    >
                      {t('pageJobs.apply') || 'Apply'}
                    </Button>
                  ) : (
                    <ForceSignInWrapper>
                      <Button
                        onClick={onApplyClick}
                        border="1px solid gray"
                        borderRadius="50px"
                        bg="none"
                        color="#5B5B5B"
                        fontSize="14px"
                      >
                        {t('pageJobs.apply') || 'Apply'}
                      </Button>
                    </ForceSignInWrapper>
                  )}
                  <Button
                    onClick={onViewDetailsClick}
                    border="1px solid gray"
                    borderRadius="50px"
                    bg="none"
                    color="#5B5B5B"
                    fontSize="14px"
                  >
                    {t('pageJobs.viewDetails') || 'View Details'}
                  </Button>
                  <IconButton
                    isLoading={saveLoading}
                    onClick={onSaveClick}
                    border="1px solid gray"
                    borderRadius="50px"
                    bg="none"
                    color="#5B5B5B"
                    fontSize="14px"
                  >
                    {isSaved ? (
                      <SavedJobIcon width="20" height="20" />
                    ) : (
                      <SaveJobs width="20" height="20" />
                    )}
                  </IconButton>
                </HStack>
              )}
            </ForceSignInWrapper>
          </HStack>
        </Stack>
      </Stack>
    );
  }
);
